import type { MapSearchLocationKind } from '~/models/mapSearchIntent'
import { STATE_LABELS } from '~/utils/constants'

const SUPPORTED_COUNTRIES = new Set(['CA', 'US'])
const ADDRESS_RESULT_TYPES = new Set([
  'street_address',
  'route',
  'intersection',
  'premise',
  'subpremise',
])
const CITY_RESULT_TYPES = new Set([
  'locality',
  'sublocality',
  'administrative_area_level_3',
])

export type GooglePlaceData = {
  address: string
  city: string
  county: string
  postalCode: string
  stateAbbr: string
  lat: number
  lng: number
  zoom: number
}

type OrganizationPlaceSearchBounds = readonly (readonly number[])[]
type OrganizationInitialViewState = {
  latitude?: number
  longitude?: number
}

export type GooglePlacePredictionOptions = {
  locationBias?: google.maps.LatLngLiteral
  locationRestriction?: google.maps.LatLngBounds
}

type GooglePredictionRankingOptions = {
  preferredStateAbbrs?: readonly string[]
}

export function getGooglePlaceSearchBoundsSignature(
  bounds?: OrganizationPlaceSearchBounds | null
) {
  return JSON.stringify(bounds ?? null)
}

export function getGooglePlaceLocationBias(
  initialViewState?: OrganizationInitialViewState | null
) {
  const latitude = initialViewState?.latitude
  const longitude = initialViewState?.longitude

  if (
    typeof latitude !== 'number' ||
    !Number.isFinite(latitude) ||
    typeof longitude !== 'number' ||
    !Number.isFinite(longitude)
  ) {
    return undefined
  }

  return { lat: latitude, lng: longitude }
}

export function buildGooglePlacePredictionRequestKey(
  query: string,
  boundsSignature: string
) {
  return JSON.stringify([query, boundsSignature])
}

export function isGooglePlacePredictionRequestPending(
  requestKey: string | null,
  settledRequestKey: string | null
) {
  return requestKey !== null && requestKey !== settledRequestKey
}

function getGoogleMapsRuntime(): typeof google.maps | null {
  const host = globalThis as typeof globalThis & {
    google?: { maps?: typeof google.maps }
  }

  return host.google?.maps ?? null
}

function hasResultType(resultTypes: readonly string[], allowed: Set<string>) {
  return resultTypes.some((type) => allowed.has(type))
}

export function isGoogleResultKind(
  resultTypes: readonly string[],
  kind: MapSearchLocationKind
) {
  switch (kind) {
    case 'address':
      return hasResultType(resultTypes, ADDRESS_RESULT_TYPES)
    case 'city':
      return hasResultType(resultTypes, CITY_RESULT_TYPES)
    case 'county':
      return resultTypes.includes('administrative_area_level_2')
    case 'postalCode':
      return resultTypes.includes('postal_code')
    case 'state':
      return resultTypes.includes('administrative_area_level_1')
    case 'unknown':
      return true
  }
}

function normalizeGoogleSearchText(value: string) {
  return value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function getPredictionMainText(
  prediction: google.maps.places.AutocompletePrediction
) {
  return (
    prediction.structured_formatting?.main_text ||
    prediction.description.split(',')[0] ||
    prediction.description
  )
}

function getPredictionStateAbbr(
  prediction: google.maps.places.AutocompletePrediction
) {
  const locationTexts = [
    prediction.structured_formatting?.secondary_text,
    prediction.description,
  ].filter((value): value is string => Boolean(value))

  for (const locationText of locationTexts) {
    const stateAbbr = getStateAbbrFromLocationText(locationText)
    if (stateAbbr) return stateAbbr
  }

  return undefined
}

function getStateAbbrFromLocationText(locationText: string) {
  const stateMatch = locationText.match(
    /(?:^|,\s*)([A-Za-z]{2})(?=\s*(?:,|$))/
  )
  if (stateMatch?.[1]) return stateMatch[1].toUpperCase()

  const normalizedLocationText = normalizeGoogleSearchText(locationText)
  const state = Object.entries(STATE_LABELS).find(([, label]) =>
    normalizedLocationText.includes(normalizeGoogleSearchText(label))
  )
  return state?.[0]
}

function getPreferredStateAbbrs(preferredStateAbbrs: readonly string[]) {
  return new Set(
    preferredStateAbbrs
      .map((state) => state.trim().toUpperCase())
      .filter((state) => state in STATE_LABELS)
  )
}

function getStateFallbackQuery({
  query,
  predictions,
  preferredStateAbbrs,
}: {
  query: string
  predictions: readonly google.maps.places.AutocompletePrediction[]
  preferredStateAbbrs: readonly string[]
}) {
  const preferredStates = getPreferredStateAbbrs(preferredStateAbbrs)
  if (preferredStates.size !== 1 || getStateAbbrFromLocationText(query)) {
    return null
  }

  const hasPreferredPrediction = predictions.some((prediction) =>
    preferredStates.has(getPredictionStateAbbr(prediction) ?? '')
  )
  if (hasPreferredPrediction) return null

  return `${query}, ${[...preferredStates][0]}`
}

export function rankGooglePredictions(
  predictions: readonly google.maps.places.AutocompletePrediction[],
  query: string,
  kind: MapSearchLocationKind,
  { preferredStateAbbrs = [] }: GooglePredictionRankingOptions = {}
) {
  const normalizedQuery = normalizeGoogleSearchText(query)
  const preferredStates = getPreferredStateAbbrs(preferredStateAbbrs)

  return predictions
    .map((prediction, providerIndex) => ({ prediction, providerIndex }))
    .filter(({ prediction }) => isGoogleResultKind(prediction.types, kind))
    .map((entry) => {
      const normalizedMainText = normalizeGoogleSearchText(
        getPredictionMainText(entry.prediction)
      )
      const normalizedDescription = normalizeGoogleSearchText(
        entry.prediction.description
      )
      const isExact =
        normalizedQuery.length > 0 &&
        (normalizedMainText === normalizedQuery ||
          normalizedDescription === normalizedQuery)

      const distanceMeters = entry.prediction.distance_meters
      const distanceRank =
        typeof distanceMeters === 'number' && Number.isFinite(distanceMeters)
          ? distanceMeters
          : Number.POSITIVE_INFINITY
      const stateRank =
        preferredStates.size === 0 ||
        preferredStates.has(getPredictionStateAbbr(entry.prediction) ?? '')
          ? 0
          : 1

      return { ...entry, stateRank, exactRank: isExact ? 0 : 1, distanceRank }
    })
    .sort(
      (left, right) =>
        left.stateRank - right.stateRank ||
        left.exactRank - right.exactRank ||
        left.distanceRank - right.distanceRank ||
        left.providerIndex - right.providerIndex
    )
    .map(({ prediction }) => prediction)
}

function findAddressComponent(
  result: google.maps.GeocoderResult,
  types: readonly string[]
) {
  for (const type of types) {
    const component = result.address_components.find((candidate) =>
      candidate.types.includes(type)
    )
    if (component) return component
  }
}

function getAddress(result: google.maps.GeocoderResult) {
  return [
    findAddressComponent(result, ['street_number']),
    findAddressComponent(result, ['intersection']),
    findAddressComponent(result, ['route']),
  ]
    .filter((component): component is google.maps.GeocoderAddressComponent =>
      Boolean(component)
    )
    .map((component) => component.short_name)
    .join(' ')
}

export function extractGooglePlaceData(
  result: google.maps.GeocoderResult
): GooglePlaceData {
  const city = findAddressComponent(result, [
    'locality',
    'sublocality',
    'administrative_area_level_3',
  ])
  const county = findAddressComponent(result, ['administrative_area_level_2'])
  const postalCode = findAddressComponent(result, ['postal_code'])
  const state = findAddressComponent(result, ['administrative_area_level_1'])

  return {
    address: getAddress(result),
    city: city?.long_name ?? '',
    county: county?.long_name ?? '',
    postalCode: postalCode?.long_name ?? '',
    stateAbbr: state?.short_name ?? '',
    lat: result.geometry.location.lat(),
    lng: result.geometry.location.lng(),
    zoom: result.types.includes('political') ? 9 : 14,
  }
}

function isSupportedCountry(result: google.maps.GeocoderResult) {
  const country = findAddressComponent(result, ['country'])
  return SUPPORTED_COUNTRIES.has(country?.short_name.toUpperCase() ?? '')
}

export function selectGoogleGeocoderResult(
  results: readonly google.maps.GeocoderResult[],
  kind: MapSearchLocationKind
) {
  return (
    results.find(
      (result) =>
        isSupportedCountry(result) && isGoogleResultKind(result.types, kind)
    ) ?? null
  )
}

function isGoogleStatus(
  status: string,
  runtimeStatus: string | undefined,
  expectedStatus: string
) {
  return status === expectedStatus || status === runtimeStatus
}

export async function getGooglePlacePredictions(
  input: string,
  { locationBias, locationRestriction }: GooglePlacePredictionOptions = {}
): Promise<google.maps.places.AutocompletePrediction[]> {
  const maps = getGoogleMapsRuntime()
  if (!maps?.places?.AutocompleteService || input.trim().length === 0) {
    return []
  }

  return new Promise((resolve, reject) => {
    try {
      const service = new maps.places.AutocompleteService()
      service.getPlacePredictions(
        {
          input,
          types: ['geocode'],
          locationBias,
          locationRestriction,
          origin: locationBias,
          componentRestrictions: { country: ['us', 'ca'] },
        },
        (predictions, status) => {
          if (
            isGoogleStatus(status, maps.places.PlacesServiceStatus?.OK, 'OK')
          ) {
            resolve(predictions ?? [])
            return
          }

          if (
            isGoogleStatus(
              status,
              maps.places.PlacesServiceStatus?.ZERO_RESULTS,
              'ZERO_RESULTS'
            )
          ) {
            resolve([])
            return
          }

          reject(new Error('Google place prediction failed.'))
        }
      )
    } catch {
      reject(new Error('Google place prediction failed.'))
    }
  })
}

async function geocodeGoogleRequest(
  request: google.maps.GeocoderRequest
): Promise<google.maps.GeocoderResult[]> {
  const maps = getGoogleMapsRuntime()
  if (!maps?.Geocoder) return []

  return new Promise((resolve, reject) => {
    try {
      const geocoder = new maps.Geocoder()
      geocoder.geocode(request, (results, status) => {
        if (isGoogleStatus(status, maps.GeocoderStatus?.OK, 'OK')) {
          resolve(results ?? [])
          return
        }

        if (
          isGoogleStatus(
            status,
            maps.GeocoderStatus?.ZERO_RESULTS,
            'ZERO_RESULTS'
          )
        ) {
          resolve([])
          return
        }

        reject(new Error('Google geocoding failed.'))
      })
    } catch {
      reject(new Error('Google geocoding failed.'))
    }
  })
}

export async function getGooglePlaceData(placeId: string) {
  if (!placeId) return null

  const results = await geocodeGoogleRequest({ placeId })
  return results[0] ? extractGooglePlaceData(results[0]) : null
}

export async function resolveGoogleLocationQuery({
  query,
  kind,
  locationBias,
  locationRestriction,
  preferredStateAbbrs,
}: {
  query: string
  kind: MapSearchLocationKind
  locationBias?: google.maps.LatLngLiteral
  locationRestriction?: google.maps.LatLngBounds
  preferredStateAbbrs?: readonly string[]
}) {
  const normalizedQuery = query.trim()
  if (!normalizedQuery || !getGoogleMapsRuntime()) return null

  const initialPredictions = await getGooglePlacePredictions(normalizedQuery, {
    locationBias,
    locationRestriction,
  })
  const stateFallbackQuery = getStateFallbackQuery({
    query: normalizedQuery,
    predictions: initialPredictions,
    preferredStateAbbrs: preferredStateAbbrs ?? [],
  })
  const fallbackPredictions = stateFallbackQuery
    ? await getGooglePlacePredictions(stateFallbackQuery, {
        locationBias,
        locationRestriction,
      })
    : []
  const predictions =
    fallbackPredictions.length > 0 ? fallbackPredictions : initialPredictions
  const predictionQuery =
    fallbackPredictions.length > 0
      ? stateFallbackQuery!
      : normalizedQuery

  const prediction = rankGooglePredictions(
    predictions,
    predictionQuery,
    kind,
    { preferredStateAbbrs }
  )[0]
  if (prediction) return getGooglePlaceData(prediction.place_id)

  const results = await geocodeGoogleRequest({
    address: stateFallbackQuery ?? normalizedQuery,
    bounds: locationRestriction,
  })
  const result = selectGoogleGeocoderResult(results, kind)
  return result ? extractGooglePlaceData(result) : null
}

export function createGoogleLocationRestriction(
  bounds?: OrganizationPlaceSearchBounds | null
) {
  const southWest = bounds?.[0]
  const northEast = bounds?.[1]
  const coordinates = [
    southWest?.[0],
    southWest?.[1],
    northEast?.[0],
    northEast?.[1],
  ]

  if (
    coordinates.some(
      (coordinate) =>
        typeof coordinate !== 'number' || !Number.isFinite(coordinate)
    )
  ) {
    return undefined
  }

  const maps = getGoogleMapsRuntime()
  if (!maps?.LatLng || !maps?.LatLngBounds) return undefined

  try {
    return new maps.LatLngBounds(
      new maps.LatLng(southWest![0]!, southWest![1]!),
      new maps.LatLng(northEast![0]!, northEast![1]!)
    )
  } catch {
    return undefined
  }
}
